// The GitKraken-style staging view (design 2.4.1): the right pane of the
// immersive Git client when the working tree is the focus. It reads status for a
// resolved repo root and splits the changed files into two sections,
//
//   Unstaged Files  = entries with a non-`.` worktree code (INCLUDES untracked)
//   Staged   Files  = entries with a real index code (a non-`.`, non-`?` side)
//
// so an untracked file shows only under Unstaged, while a file edited again after
// being staged shows in BOTH sections (matching how git reports it). On top sits a
// header ("N file changes on <branch>" + discard-all), a Path / Tree grouping
// toggle with an A-Z sort, then the two sections (each with a stage-all /
// unstage-all action and per-row stage / unstage), and finally a commit composer:
// a single-line Summary with a live counter, a multiline Description, an
// "Amend previous commit" checkbox, and an adaptive primary button that either
// commits the staged changes or stages-everything-then-commits.
//
// Mutations run through a busy guard; on success they notify the client
// (`onMutated`) and refetch status; failures surface as an error toast. Discards
// are destructive, so they wait behind an inline two-button confirm (never
// `window.confirm`).

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
} from 'react';
import type { GitApi, GitFileStatus, GitStatus, GitStatusCode } from '@slate/core';
import { ArrowDownAZ, GitBranch, Minus, Plus, Trash2 } from 'lucide-react';
import { basename, dirOf } from '../../fs/containment';
import { gitStatusIcon } from '../gitStatusIcon';
import { useContextMenu, type ContextMenuEntry } from '../../context-menu';
import { toast } from '../../store/toastStore';

export interface GitStagingViewProps {
  /** The repo to operate on, or null when no repo resolves. */
  root: string | null;
  /** The platform GitApi (guarded; undefined when unavailable). */
  git: GitApi | undefined;
  /** Bumped by the client to force a re-read after a cross-pane mutation. */
  refreshToken: number;
  /** Notify the client a mutation happened so the other panes refresh. */
  onMutated: () => void;
}

type ViewMode = 'path' | 'tree';
type RowAction = 'stage' | 'unstage';

const VIEW_KEY = 'slate:git:staging:view';
const SORT_KEY = 'slate:git:staging:sort';
/** A summary longer than this gets the "over length" styling hint. */
const SUMMARY_SOFT_MAX = 72;

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota: persistence is best-effort. */
  }
}

/** A real staged change: a non-`.` index side that is not the untracked marker. */
function isStaged(f: GitFileStatus): boolean {
  return f.index !== '.' && f.index !== '?';
}
/** Any working-tree change, including untracked (`?`). */
function isUnstaged(f: GitFileStatus): boolean {
  return f.worktree !== '.';
}

function branchLabel(status: GitStatus | null): string {
  if (!status) return '';
  if (status.unborn) return 'No commits yet';
  if (status.detached) return 'Detached HEAD';
  return status.branch ?? 'HEAD';
}

interface DirGroup {
  dir: string;
  files: GitFileStatus[];
}

/** Group entries by their parent directory for the Tree view. */
function groupByDir(files: readonly GitFileStatus[], sort: boolean): DirGroup[] {
  const map = new Map<string, GitFileStatus[]>();
  for (const f of files) {
    const dir = dirOf(f.path);
    const arr = map.get(dir);
    if (arr) arr.push(f);
    else map.set(dir, [f]);
  }
  const groups: DirGroup[] = Array.from(map, ([dir, fs]) => ({
    dir,
    files: sort ? sortByName(fs) : fs,
  }));
  return sort ? groups.sort((a, b) => a.dir.localeCompare(b.dir)) : groups;
}

function sortByName(files: readonly GitFileStatus[]): GitFileStatus[] {
  return [...files].sort((a, b) => basename(a.path).localeCompare(basename(b.path)));
}
function sortByPath(files: readonly GitFileStatus[]): GitFileStatus[] {
  return [...files].sort((a, b) => a.path.localeCompare(b.path));
}

interface RowProps {
  file: GitFileStatus;
  code: GitStatusCode;
  action: RowAction;
  showDir: boolean;
  disabled: boolean;
  onAct: () => void;
  onContextMenu?: (e: MouseEvent) => void;
}

function StagingRow({
  file,
  code,
  action,
  showDir,
  disabled,
  onAct,
  onContextMenu,
}: RowProps): ReactElement {
  const { Icon, tone } = gitStatusIcon(code);
  const dir = dirOf(file.path);
  const verb = action === 'stage' ? 'Stage' : 'Unstage';
  return (
    <li className="mp-git-staging__row" onContextMenu={onContextMenu}>
      <Icon size={13} aria-hidden className={`mp-git-staging__row-icon mp-git-status--${tone}`} />
      <span className="mp-git-staging__row-path" title={file.path}>
        {showDir && dir && <span className="mp-git-staging__row-dir">{dir}/</span>}
        <span className="mp-git-staging__row-name">{basename(file.path)}</span>
      </span>
      <button
        type="button"
        className="mp-iconbtn mp-git-staging__row-act"
        aria-label={`${verb} ${file.path}`}
        title={verb}
        disabled={disabled}
        onClick={onAct}
      >
        {action === 'stage' ? <Plus size={13} aria-hidden /> : <Minus size={13} aria-hidden />}
      </button>
    </li>
  );
}

interface SectionProps {
  title: string;
  files: GitFileStatus[];
  action: RowAction;
  codeOf: (f: GitFileStatus) => GitStatusCode;
  bulkLabel: string;
  onBulk: () => void;
  onRow: (path: string) => void;
  onRowMenu?: (e: MouseEvent, file: GitFileStatus) => void;
  view: ViewMode;
  sort: boolean;
  disabled: boolean;
}

function Section({
  title,
  files,
  action,
  codeOf,
  bulkLabel,
  onBulk,
  onRow,
  onRowMenu,
  view,
  sort,
  disabled,
}: SectionProps): ReactElement | null {
  if (files.length === 0) return null;
  const renderRow = (f: GitFileStatus, showDir: boolean): ReactElement => (
    <StagingRow
      key={`${action}:${f.path}`}
      file={f}
      code={codeOf(f)}
      action={action}
      showDir={showDir}
      disabled={disabled}
      onAct={() => onRow(f.path)}
      onContextMenu={onRowMenu ? (e) => onRowMenu(e, f) : undefined}
    />
  );
  return (
    <section className="mp-git-staging__section" aria-label={title}>
      <header className="mp-git-staging__section-head">
        <h3 className="mp-git-staging__section-title">
          {title} ({files.length})
        </h3>
        <button
          type="button"
          className="mp-btn mp-git-staging__bulk"
          disabled={disabled}
          onClick={onBulk}
        >
          {bulkLabel}
        </button>
      </header>
      {view === 'tree' ? (
        groupByDir(files, sort).map((g) => (
          <div className="mp-git-staging__group" key={`${action}:dir:${g.dir}`}>
            <div className="mp-git-staging__group-dir" data-testid="git-staging-dirgroup">
              {g.dir === '' ? '/' : g.dir}
            </div>
            <ul className="mp-git-staging__list">{g.files.map((f) => renderRow(f, false))}</ul>
          </div>
        ))
      ) : (
        <ul className="mp-git-staging__list">
          {(sort ? sortByPath(files) : files).map((f) => renderRow(f, true))}
        </ul>
      )}
    </section>
  );
}

interface PendingDiscard {
  message: string;
  paths: string[];
}

export function GitStagingView({
  root,
  git,
  refreshToken,
  onMutated,
}: GitStagingViewProps): ReactElement {
  const { openContextMenu } = useContextMenu();
  const [status, setStatus] = useState<GitStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [amend, setAmend] = useState(false);
  const amendPrefilledRef = useRef(false);

  const [view, setView] = useState<ViewMode>(() =>
    readStored(VIEW_KEY) === 'tree' ? 'tree' : 'path',
  );
  const [sort, setSort] = useState<boolean>(() => readStored(SORT_KEY) === '1');
  const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(null);

  useEffect(() => writeStored(VIEW_KEY, view), [view]);
  useEffect(() => writeStored(SORT_KEY, sort ? '1' : '0'), [sort]);

  // Re-read status when the repo / token changes; cancel a superseded fetch so a
  // slow earlier response can't clobber a newer one.
  useEffect(() => {
    if (!root || !git) {
      setStatus(null);
      setError(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const s = await git.status(root);
        if (cancelled) return;
        setStatus(s);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setStatus(null);
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [root, git, refreshToken]);

  // Lightweight refetch used after a mutation and on window focus (an external
  // git change may have landed while we were away).
  const refresh = useCallback(async (): Promise<void> => {
    if (!root || !git) {
      setStatus(null);
      return;
    }
    try {
      const s = await git.status(root);
      setStatus(s);
      setError(null);
    } catch (e) {
      setStatus(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [root, git]);

  useEffect(() => {
    const onFocus = (): void => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const files = useMemo(() => status?.files ?? [], [status]);
  const staged = useMemo(() => files.filter(isStaged), [files]);
  const unstaged = useMemo(() => files.filter(isUnstaged), [files]);
  const changedPaths = useMemo(
    () => Array.from(new Set(files.map((f) => f.path))),
    [files],
  );

  const runMutation = useCallback(
    async (op: () => Promise<unknown>): Promise<void> => {
      if (busy) return;
      setBusy(true);
      try {
        await op();
        onMutated();
        await refresh();
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
        toast(message, { kind: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [busy, onMutated, refresh],
  );

  const stageOne = useCallback(
    (path: string): void => {
      if (!root || !git) return;
      void runMutation(() => git.stage(root, [path]));
    },
    [root, git, runMutation],
  );
  const unstageOne = useCallback(
    (path: string): void => {
      if (!root || !git) return;
      void runMutation(() => git.unstage(root, [path]));
    },
    [root, git, runMutation],
  );
  const stageAll = useCallback((): void => {
    if (!root || !git || unstaged.length === 0) return;
    void runMutation(() => git.stage(root, unstaged.map((f) => f.path)));
  }, [root, git, unstaged, runMutation]);
  const unstageAll = useCallback((): void => {
    if (!root || !git || staged.length === 0) return;
    void runMutation(() => git.unstage(root, staged.map((f) => f.path)));
  }, [root, git, staged, runMutation]);

  const confirmDiscard = useCallback((): void => {
    const target = pendingDiscard;
    setPendingDiscard(null);
    if (!root || !git || !target || target.paths.length === 0) return;
    void runMutation(() => git.discard(root, target.paths));
  }, [pendingDiscard, root, git, runMutation]);

  const openRowMenu = useCallback(
    (e: MouseEvent, file: GitFileStatus): void => {
      e.preventDefault();
      e.stopPropagation();
      if (!root || !git) return;
      const items: ContextMenuEntry[] = [
        { id: 'stage', label: 'Stage', icon: Plus, onSelect: () => stageOne(file.path) },
        {
          id: 'discard',
          label: 'Discard changes…',
          icon: Trash2,
          danger: true,
          onSelect: () =>
            setPendingDiscard({
              message: `Discard changes to ${basename(file.path)}? This cannot be undone.`,
              paths: [file.path],
            }),
        },
      ];
      openContextMenu(e.clientX, e.clientY, items);
    },
    [root, git, stageOne, openContextMenu],
  );

  // Amend prefills the composer from HEAD exactly once: re-checking after an
  // uncheck does not re-fetch (and so cannot clobber the user's edits).
  const onToggleAmend = useCallback(
    (checked: boolean): void => {
      setAmend(checked);
      if (!checked || amendPrefilledRef.current || !root || !git) return;
      amendPrefilledRef.current = true;
      void (async () => {
        try {
          const detail = await git.commitDetail(root, 'HEAD');
          setSummary(detail.subject);
          setDescription(detail.body);
        } catch (e) {
          toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
        }
      })();
    },
    [root, git],
  );

  const summaryEmpty = summary.trim().length === 0;
  const noChangesAtAll = staged.length === 0 && unstaged.length === 0;
  // A no-op amend reuses HEAD's changes (nothing newly staged); it tolerates an
  // empty summary so an amend that only re-touches the working tree still goes.
  const noopAmend = amend && staged.length === 0;
  const commitDisabled =
    busy || !root || !git || noChangesAtAll || (summaryEmpty && !noopAmend);

  const primaryLabel =
    staged.length > 0
      ? `Commit ${staged.length} file${staged.length === 1 ? '' : 's'}`
      : 'Stage all & Commit';

  const buildMessage = useCallback((): string => {
    const body = description.trim();
    return body ? `${summary}\n\n${body}` : summary;
  }, [summary, description]);

  const clearComposer = useCallback((): void => {
    setSummary('');
    setDescription('');
    setAmend(false);
    amendPrefilledRef.current = false;
  }, []);

  const commit = useCallback((): void => {
    if (commitDisabled || !root || !git) return;
    const message = buildMessage();
    const needsStageAll = staged.length === 0 && unstaged.length > 0;
    const stagePaths = unstaged.map((f) => f.path);
    void runMutation(async () => {
      if (needsStageAll) await git.stage(root, stagePaths);
      await git.commit(root, message, { amend });
      clearComposer();
    });
  }, [
    commitDisabled,
    root,
    git,
    buildMessage,
    staged.length,
    unstaged,
    amend,
    runMutation,
    clearComposer,
  ]);

  if (!root) {
    return (
      <div className="mp-git__empty">
        <GitBranch size={24} aria-hidden className="mp-git__empty-icon" />
        <p className="mp-git__empty-title">No Git repository</p>
        <p className="mp-git__empty-note">No Git repository for the active file or workspace.</p>
      </div>
    );
  }

  const branch = branchLabel(status);
  const changeCount = changedPaths.length;

  return (
    <div className="mp-git-staging">
      <header className="mp-git-staging__header">
        <span className="mp-git-staging__summary-line">
          <span className="mp-git-staging__count">{changeCount}</span>
          {` file change${changeCount === 1 ? '' : 's'} on `}
          <span className="mp-git-staging__branch">
            <GitBranch size={12} aria-hidden />
            <span className="mp-git-staging__branch-name">{branch}</span>
          </span>
        </span>
        <button
          type="button"
          className="mp-iconbtn mp-git-staging__discard-all"
          aria-label="Discard all changes"
          title="Discard all changes"
          disabled={busy || changeCount === 0}
          onClick={() =>
            setPendingDiscard({
              message: `Discard all ${changeCount} change${
                changeCount === 1 ? '' : 's'
              }? This cannot be undone.`,
              paths: changedPaths,
            })
          }
        >
          <Trash2 size={14} aria-hidden />
        </button>
      </header>

      <div className="mp-git-staging__controls">
        <div
          className="mp-git-staging__viewtoggle"
          role="group"
          aria-label="File grouping"
        >
          <button
            type="button"
            className="mp-git-staging__seg"
            aria-pressed={view === 'path'}
            onClick={() => setView('path')}
          >
            Path
          </button>
          <button
            type="button"
            className="mp-git-staging__seg"
            aria-pressed={view === 'tree'}
            onClick={() => setView('tree')}
          >
            Tree
          </button>
        </div>
        <button
          type="button"
          className="mp-iconbtn mp-git-staging__sort"
          aria-label="Sort A to Z"
          aria-pressed={sort}
          title="Sort A to Z"
          onClick={() => setSort((s) => !s)}
        >
          <ArrowDownAZ size={14} aria-hidden />
        </button>
      </div>

      {error && (
        <p className="mp-git__error" role="alert">
          {error}
        </p>
      )}

      {pendingDiscard && (
        <div className="mp-git-staging__confirm" role="alertdialog" aria-label="Confirm discard">
          <span className="mp-git-staging__confirm-msg">{pendingDiscard.message}</span>
          <div className="mp-git-staging__confirm-actions">
            <button
              type="button"
              className="mp-btn"
              onClick={() => setPendingDiscard(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mp-btn mp-btn--danger"
              onClick={confirmDiscard}
            >
              Discard
            </button>
          </div>
        </div>
      )}

      <Section
        title="Unstaged Files"
        files={unstaged}
        action="stage"
        codeOf={(f) => f.worktree}
        bulkLabel="Stage All Changes"
        onBulk={stageAll}
        onRow={stageOne}
        onRowMenu={openRowMenu}
        view={view}
        sort={sort}
        disabled={busy}
      />

      <Section
        title="Staged Files"
        files={staged}
        action="unstage"
        codeOf={(f) => f.index}
        bulkLabel="Unstage All"
        onBulk={unstageAll}
        onRow={unstageOne}
        view={view}
        sort={sort}
        disabled={busy}
      />

      {noChangesAtAll && !error && <p className="mp-git__clean">No changes.</p>}

      <form
        className="mp-git-staging__composer"
        onSubmit={(e) => {
          e.preventDefault();
          commit();
        }}
      >
        <div className="mp-git-staging__summary-field">
          <input
            type="text"
            className="mp-git-staging__summary-input"
            placeholder="Summary (required)"
            aria-label="Summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
          <span
            className="mp-git-staging__counter"
            data-over={summary.length > SUMMARY_SOFT_MAX ? '' : undefined}
            aria-hidden
          >
            {summary.length}
          </span>
        </div>
        <textarea
          className="mp-git-staging__description"
          placeholder="Description (optional)"
          aria-label="Description"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <label className="mp-git-staging__amend">
          <input
            type="checkbox"
            checked={amend}
            onChange={(e) => onToggleAmend(e.target.checked)}
          />
          <span>Amend previous commit</span>
        </label>
        <button
          type="submit"
          className="mp-btn mp-btn--primary mp-git-staging__commit"
          disabled={commitDisabled}
        >
          {primaryLabel}
        </button>
      </form>
    </div>
  );
}
